<template>
    <Modal class="mellon-webecr-recovery-modal"
           data-component="MellonWebECRRecoveryModal"
           icon="fa-solid fa-shield-plus"
           title="MELLON_WEBECR.RECOVERY_MODAL_TITLE">
        <div class="alert alert-info">
            <i class="fa-solid fa-info-circle"></i>
            {{ l('MELLON_WEBECR.RECOVERY__DESC') }}
        </div>

        <div class="text-center mt-1 user-selectable">
            <!-- Invoice Reference -->
            <div>
                <strong>
                    <i class="fa-solid fa-check"></i>
                    {{ l('MELLON_WEBECR.RECOVERY__INVOICE_REF') }}
                </strong>

                <p>
                    <span class="label label-default">{{ orderInvoiceID }}</span>
                </p>
            </div>

            <!-- Payment -->
            <div>
                <strong>
                    <i class="fa-solid"
                       :class="paymentOk === true ? 'fa-check' : 'fa-times'"></i>
                    {{ l('PAYMENT.REFERENCE') }}
                </strong>

                <!-- Payment details -->
                <p v-if="payment">
                    <span class="label"
                          :class="paymentLabelColor">
                        {{ payment.thirdPartyID }}
                    </span>
                </p>

                <!-- Payment error -->
                <p v-if="typeof paymentOk === 'string'"
                   class="text-danger text-sm">
                    {{ l(paymentOk) }}
                </p>
            </div>

            <!-- Transaction Intent -->
            <div v-if="loadingTransactionIntent">
                <i class="fa-solid fa-spinner fa-spin"></i>
                {{ l('MELLON_WEBECR.RECOVERY__LOADING_INTENT') }}
            </div>
            <div v-else-if="loadingTransactionIntent === false">
                <strong>
                    <i class="fa-solid"
                       :class="intentOk === true ? 'fa-check' : 'fa-times'"></i>
                    {{ l('MELLON_WEBECR.INTENT') }}
                </strong>

                <!-- Intent details -->
                <p v-if="transactionIntent">
                    <span class="label label-default">{{ transactionIntent.Id }}</span>
                    &nbsp;
                    <MellonWebECRIntentStatusLabel :intent="transactionIntent" />
                    &nbsp;
                    <MellonWebECRIntentResultLabel :intent="transactionIntent" />
                </p>

                <!-- Intent error -->
                <p v-if="typeof intentOk === 'string'"
                   class="text-danger text-sm">
                    {{ l(intentOk) }}
                </p>
            </div>

            <!-- Transaction -->
            <div v-if="loadingTransaction">
                <i class="fa-solid fa-spinner fa-spin"></i>
                {{ l('MELLON_WEBECR.RECOVERY__LOADING_TRANSACTION') }}
            </div>
            <div v-else-if="loadingTransaction === false">
                <strong>
                    <i class="fa-solid"
                       :class="transactionOk === true ? 'fa-check' : 'fa-times'"></i>
                    {{ l('MELLON_WEBECR.TRANSACTION') }}
                </strong>

                <!-- Transaction details -->
                <p v-if="transaction">
                    <span class="label label-default">{{ transaction.Id }}</span>
                    &nbsp;
                    <MellonWebECRTransactionResultLabel :transaction="transaction" />
                </p>

                <!-- Transaction error -->
                <p v-if="typeof transactionOk === 'string'"
                   class="text-danger text-sm">
                    {{ l(transactionOk) }}
                </p>
            </div>

            <!-- Existing Payment -->
            <div v-if="loadingExistingPayment">
                <i class="fa-solid fa-spinner fa-spin"></i>
                {{ l('MELLON_WEBECR.RECOVERY__LOADING_EXISTING') }}
            </div>
            <div v-else-if="loadingExistingPayment === false">
                <strong>
                    <i class="fa-solid"
                       :class="existingOk === true ? 'fa-check' : 'fa-times'"></i>
                    {{ l('MELLON_WEBECR.RECOVERY__EXISTING_PAYMENT') }}
                </strong>

                <!-- Existing payment details -->
                <p v-if="existingPayment">
                    <span class="label label-danger">{{ existingPayment.paymentID }}</span>
                </p>
                <p v-else>
                    <span class="label label-success">{{ l('MELLON_WEBECR.RECOVERY__NO_EXISTING') }}</span>
                </p>

                <!-- Transaction error -->
                <p v-if="typeof existingOk === 'string'"
                   class="text-danger text-sm">
                    {{ l(existingOk) }}
                </p>
            </div>
        </div>

        <template v-slot:footer>
            <div class="modl-footer">
                <SquareBtn v-if="isAbandonable"
                           size="lg"
                           color="danger"
                           class="cancel-btn"
                           @click="abandonTransaction">
                    <i class="fa-solid fa-trash-xmark"></i>
                    {{ l('MELLON_WEBECR.RECOVERY__ABANDON') }}
                </SquareBtn>
                <!-- The close and abandon buttons do the same thing, but the wording is better for the user when it's
                not really abandoning anything. -->
                <SquareBtn v-else
                           size="lg"
                           color="default"
                           class="cancel-btn"
                           @click="abandonTransaction">
                    {{ l('CLOSE') }}
                </SquareBtn>

                <SquareBtn size="lg"
                           color="danger"
                           :disabled="!isVoidable"
                           :loading="voiding"
                           @click="voidTransaction">
                    <i class="fa-solid fa-ban"></i>
                    {{ l('MELLON_WEBECR.VOID') }}
                </SquareBtn>

                <span class="flex--grow"></span>

                <SquareBtn size="lg"
                           color="default"
                           :disabled="voiding || loading"
                           :loading="loading"
                           @click="init">
                    <i class="fa-solid fa-arrow-rotate-right"></i>
                    {{ l('REFRESH') }}
                </SquareBtn>

                <SquareBtn size="lg"
                           color="success"
                           :disabled="voiding || loading || !canUse"
                           @click="useTransaction">
                    <i class="fa-solid fa-check"></i>
                    {{ l('MELLON_WEBECR.RECOVERY__USE') }}
                </SquareBtn>
            </div>
        </template>
    </Modal>
</template>

<script>
import MellonWebECRIntentResult from '@/lib/PaymentMethods/MellonWebECR/Enums/MellonWebECRIntentResult';
import MellonWebECRIntentResultLabel
    from '@/epos/vue/modals/Payment/PaymentMethods/MellonWebECR/MellonWebECRIntentResultLabel';
import MellonWebECRIntentStatus from '@/lib/PaymentMethods/MellonWebECR/Enums/MellonWebECRIntentStatus';
import MellonWebECRIntentStatusLabel
    from '@/epos/vue/modals/Payment/PaymentMethods/MellonWebECR/MellonWebECRIntentStatusLabel';
import MellonWebECRTransactionResultLabel
    from '@/epos/vue/modals/Payment/PaymentMethods/MellonWebECR/MellonWebECRTransactionResultLabel';
import Modal from '@/vue/components/Modals/Modal';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';
import { generateOrderInvoiceID } from '@/lib/Invoicing';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

/**
 * @typedef {object} MellonWebECRRecoveryResult
 * @property {MellonWebECRPayment} [payment]
 * @property {MellonWebECRTransactionIntent} [transactionIntent]
 * @property {MellonWebECRTransaction} [transaction]
 * @property {boolean} [abandon]
 */

/**
 * The MellonWebECR recovery modal is shown when a Mellon transaction was started and saved in the cart, then something
 * happened (most likely the page was reloaded). We need to check what happened to that transaction and if it can be
 * added as a payment to the current cart/order.
 *
 * - Extract the payment from the transaction in progress.
 * - Load the transaction intent from Mellon using the payment's third party ID as the customer reference.
 * - Load the transaction from Mellon using the transaction intent's transaction ID.
 * - Check if the payment, transaction intent and transaction are all valid and suitable for this order.
 *
 * Actions the user can perform are:
 * - Abandon the payment: This will clear the transaction in progress, close the modal, and do nothing else.
 * - Void the payment: If the transaction exists and is not voided, the user can void the transaction. This will
 *   send a void request to Mellon and update the displayed status, without closing the modal.
 * - Refresh: Reload the transaction intent and transaction data from Mellon. This will not close the modal.
 * - Use the payment: If the payment, transaction intent and transaction are all valid and suitable for this order,
 *   the user can add this payment to the cart. This will close the modal and return to the WebECR payment method.
 */
export default {
    components: {
        MellonWebECRIntentResultLabel,
        MellonWebECRIntentStatusLabel,
        MellonWebECRTransactionResultLabel,
        SquareBtn,
        Modal,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        /**
         * @type {Order}
         */
        order: {
            type: Object,
            required: true,
        },

        /**
         * @type {TransactionInProgress}
         */
        transactionInProgress: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            /**
             * @type {MellonWebECRPayment}
             */
            payment: null,

            /**
             * @type {?boolean} - Starts as null, true while loading, false when finished loading.
             */
            loading: null,

            /**
             * @type {?boolean} - Starts as null, true while loading, false when finished loading.
             */
            loadingTransactionIntent: null,

            /**
             * @type {?MellonWebECRTransactionIntent}
             */
            transactionIntent: null,

            /**
             * @type {?boolean} - Starts as null, true while loading, false when finished loading.
             */
            loadingTransaction: null,

            /**
             * @type {?MellonWebECRTransaction}
             */
            transaction: null,

            /**
             * @type {?boolean} - Starts as null, true while loading, false when finished loading.
             */
            loadingExistingPayment: null,

            /**
             * A payment already saved on DigiTickets with the same thirdPartyID as this.
             *
             * @type {?Payment}
             */
            existingPayment: null,

            /**
             * @type {?boolean} - Starts as null, true while loading, false when finished loading.
             */
            voiding: null,
        };
    },

    computed: {
        /**
         * The expected invoice ID for this order (note: the order, not the payment).
         * This should be the first part of the payment's thirdPartyID/CustomerReference.
         * e.g. "D12345-credit-1"
         *
         * @returns {string}
         */
        orderInvoiceID() {
            return generateOrderInvoiceID(this.order, this.order.total < 0);
        },

        /**
         * Is the payment suitable to be used for this order?
         * - The payment must exist
         * - The payment must have a third party ID
         * - The third party ID must start with the expected invoice ID
         *
         * @returns {string|true} - True if valid or a lang key if not.
         */
        paymentOk() {
            if (!this.payment) {
                return 'MELLON_WEBECR.RECOVERY__NO_PAYMENT';
            }

            if (!this.payment.thirdPartyID) {
                return 'MELLON_WEBECR.RECOVERY__NO_PAYMENT_REF';
            }

            if (!this.payment.thirdPartyID.startsWith(this.orderInvoiceID)) {
                return 'MELLON_WEBECR.RECOVERY__INVALID_REF';
            }

            return true;
        },

        paymentLabelColor() {
            if (this.paymentOk !== true) {
                return 'label-danger';
            }

            return 'label-success';
        },

        /**
         * Is the transaction intent suitable to be used for this order?
         * - The transaction intent must exist
         * - The transaction intent must have a status of completed
         * - The transaction intent must have a result of approved
         *
         * @returns {string|true} - True if valid or a lang key if not.
         */
        intentOk() {
            if (!this.transactionIntent) {
                return 'MELLON_WEBECR.RECOVERY__NO_INTENT';
            }

            if (this.transactionIntent.Status !== MellonWebECRIntentStatus.COMPLETED) {
                return 'MELLON_WEBECR.RECOVERY__INVALID_INTENT_STATUS';
            }

            if (this.transactionIntent.Result !== MellonWebECRIntentResult.APPROVED) {
                return 'MELLON_WEBECR.RECOVERY__INVALID_INTENT_RESULT';
            }

            return true;
        },

        /**
         * Is the transaction suitable to be used for this order?
         * - The transaction must exist
         * - The transaction must have been approved
         * - The transaction must not have been voided
         *
         * @returns {string|true} - True if valid or a lang key if not.
         */
        transactionOk() {
            if (!this.transaction) {
                return 'MELLON_WEBECR.RECOVERY__NO_TRANSACTION';
            }

            if (!this.transaction.Approved) {
                return 'MELLON_WEBECR.RECOVERY__TRANSACTION_NOT_APPROVED';
            }

            if (this.transaction.Voided) {
                return 'MELLON_WEBECR.RECOVERY__TRANSACTION_VOIDED';
            }

            return true;
        },

        /**
         * Is there any issue with an existing payment?
         * - No existing payment with this thirdPartyID should exist.
         *
         * @returns {string|true} - True if valid or a lang key if not.
         */
        existingOk() {
            if (this.existingPayment) {
                return 'MELLON_WEBECR.RECOVERY__EXISTING_FOUND';
            }

            return true;
        },

        /**
         * Is the whole payment suitable to be used for this order?
         *
         * @returns {boolean}
         */
        canUse() {
            return this.paymentOk === true
                && this.intentOk === true
                && this.transactionOk === true
                && this.existingOk === true;
        },

        /**
         * Returns true if closing the modal would be considered abandoning the payment,
         * or false if it's just closing.
         */
        isAbandonable() {
            // Unless a payment has already been saved on DT with the same thirdPartyID, we'll consider this
            // abandoning the payment.
            return !this.existingPayment;
        },

        isVoidable() {
            return !this.loading // Not currently loading
                && this.transactionOk === true // Transaction is valid
                && this.transaction.Voided === false // Transaction is not already voided
                && !this.existingPayment; // Payment has not already been created
        },
    },

    created() {
        // Load the required data on startup.
        this.init();
    },

    methods: {

        reset() {
            this.payment = null;
            this.loading = null;
            this.loadingTransactionIntent = null;
            this.transactionIntent = null;
            this.loadingTransaction = null;
            this.transaction = null;
            this.loadingExistingPayment = null;
            this.existingPayment = null;
            this.voiding = null;
        },

        async init() {
            // Reset the state of the modal.
            this.reset();

            this.loading = true;

            // The txnInProgress should contain at least a payment.
            // It may also include a transactionIntent, and maybe even a transaction.

            this.payment = this.transactionInProgress.state.payment;

            if (this.transactionInProgress.transactionIntent?.Id) {
                // The txnInProgress contained an intent so we can fetch its data by ID.
                await this.loadTransactionIntentByID(this.transactionInProgress.transactionIntent.Id);
            } else {
                // There was no intent saved, but we can it up the intent by thirdPartyID (CustomerReference).
                await this.loadTransactionIntentByCustomerReference(this.payment.thirdPartyID);
            }

            if (this.transactionIntent?.Transaction) {
                // We got an intent, and it has a transaction ID, so we can load the transaction.
                await this.loadTransaction(this.transactionIntent.Transaction);
            }

            if (this.payment.thirdPartyID) {
                await this.loadExistingPayment();
            }

            this.loading = false;
        },

        async loadTransactionIntentByID(intentID) {
            this.loadingTransactionIntent = true;
            return container.mellonWebECRDriver.client
                .getTransactionIntent(intentID)
                .then((response) => {
                    this.transactionIntent = response.data;
                })
                .catch((error) => {
                    console.error('Error loading transaction intent:', error);
                })
                .finally(() => {
                    this.loadingTransactionIntent = false;
                });
        },

        async loadTransactionIntentByCustomerReference(customerReference) {
            this.loadingTransactionIntent = true;
            return container.mellonWebECRDriver.client
                .getTransactionIntents({
                    CustomerReference: customerReference,
                })
                .then((response) => {
                    if (response?.results?.length > 0) {
                        this.transactionIntent = response.results[0];
                    } else {
                        console.error('No transaction intent found by customer reference');
                        this.transactionIntent = null;
                    }
                })
                .catch((error) => {
                    console.error('Error loading transaction intent by customer reference:', error);
                    this.transactionIntent = null;
                })
                .finally(() => {
                    this.loadingTransactionIntent = false;
                });
        },

        async loadTransaction(transactionID) {
            this.loadingTransaction = true;
            return container.mellonWebECRDriver.client
                .getTransaction(transactionID)
                .then((response) => {
                    this.transaction = response;
                })
                .catch((error) => {
                    console.error('Error loading transaction:', error);
                    this.transaction = null;
                })
                .finally(() => {
                    this.loadingTransaction = false;
                });
        },

        async loadExistingPayment() {
            this.loadingExistingPayment = true;
            return container.apiClient
                .request(
                    'GET',
                    'payments/by-third-party-id',
                    {
                        thirdPartyIDs: [
                            this.payment.thirdPartyID,
                        ],
                    },
                )
                .then(({ data }) => {
                    if (data?.length > 0) {
                        this.existingPayment = data[0];
                    } else {
                        this.existingPayment = null;
                    }
                })
                .catch((err) => {
                    console.error('Error checking for saved payment:', err);
                    this.existingPayment = null;
                })
                .finally(() => {
                    this.loadingExistingPayment = false;
                });
        },

        abandonTransaction() {
            this.resolve({
                abandon: true,
            });
        },

        useTransaction() {
            this.resolve({
                payment: this.payment,
                transactionIntent: this.transactionIntent,
                transaction: this.transaction,
            });
        },

        async voidTransaction() {
            this.voiding = true;

            return container.mellonWebECRDriver
                .voidTransaction(
                    this.transactionIntent.Terminal,
                    this.transactionIntent.Id,
                )
                .then(() => {
                    this.init();
                })
                .finally(() => {
                    this.voiding = false;
                });
        },
    },
};
</script>

<style lang="less">
@import "../../../../../../less/variables.less";

.mellon-webecr-recovery-modal {
    .user-selectable > div {
        margin-top: @gutter-width;

        p {
            margin-top: @gutter-width / 4;
            margin-bottom: 0;
        }

        .label {
            user-select: text;
        }
    }
}
</style>
